import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { useCallback } from "react";

import { APPLY_EXTRACTED_BILL_OF_LADING, EXTRACTED_BILL_OF_LADING } from "./api";
import { toApplyInput } from "./extractionReview";

/**
 * Applying a reviewed bill of lading reading.
 *
 * There is no `wasteShippingDocumentInputValidator` (unlike 7C's profile
 * apply), so nothing runs client-side before the mutation — the save either
 * succeeds or surfaces the server's rejection.
 *
 * Refetches the reading, whose `candidateStatus` flips to APPLIED. Nothing
 * else is refetched here: a fresh apply always navigates to a route that
 * reads the saved document for the first time.
 */
export function useApplyReviewedBillOfLading({
  documentId,
  facilityId,
  existingId,
  onApplied,
}: {
  documentId: string;
  facilityId: string;
  existingId?: string;
  onApplied: (savedId: string) => void;
}) {
  const alerts = useAlerts();
  const [applyMutation, { loading: applying }] = useMutation(APPLY_EXTRACTED_BILL_OF_LADING, {
    refetchQueries: [EXTRACTED_BILL_OF_LADING],
  });

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      try {
        const { data } = await applyMutation({
          variables: { input: toApplyInput(reviewed, { documentId, facilityId, existingId }) },
        });
        const savedId = data?.applyExtractedBillOfLading?.id;
        if (!savedId) {
          throw new Error("The apply did not return a saved document.");
        }
        alerts.success("Bill of lading applied.");
        onApplied(savedId);
      } catch (error) {
        alerts.error(
          "Could not apply the bill of lading",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [alerts, applyMutation, documentId, facilityId, existingId, onApplied]
  );

  return { confirm, applying };
}
